import { MouseEvent, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useParams, useRouter } from 'next/navigation';
import { useEvent, useUnit } from 'effector-react';
import { calculateContextMenuPosition, openContextMenuEvent } from '@nx-frontend/shared/ui/context-menu';
import { closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { useCreateNote } from '@nx-frontend/shared/hooks';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import {
  $workspaceTags,
  addTagToNoteEvent,
  deleteWorkspaceTagEvent,
  renameWorkspaceTagEvent,
} from '@nx-frontend/shared/store/tags';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { $userGranularPermissions } from '@nx-frontend/shared/store/user-granular-permissions';
import { $workspaceAccess, $wpCanManageMembers } from '@nx-frontend/shared/store/workspace-access';
import { webnotesThemeColors, webnotesThemeStyle } from '@nx-frontend/shared/themes/webnotes';
import {
  ContextMenuItemData,
  MenuItem,
  PopupType,
  UiContextMenuConfigPosition,
  UiContextMenuTheme,
} from '@nx-frontend/shared/types';
import { getFilterMenuItemsByPrivileges, tagsComparator } from '@nx-frontend/shared/utils';
import { contextItemLocMap, createPageWithTagItem, deleteItem, renameMenuItem } from './context-items';

export const useOpenKitchenMenuContextTag = () => {
  const [currentWorkspace, unleashFlags, userGranularPermissions, workspaceAccess, wpCanManageMembers, workspaceTags] =
    useUnit([
      $currentWorkspace,
      $unleashFlags,
      $userGranularPermissions,
      $workspaceAccess,
      $wpCanManageMembers,
      $workspaceTags,
    ]);
  const [openContextMenu, openModal, closeModal, addTagToNote, deleteWorkspaceTag, renameWorkspaceTag] = useEvent([
    openContextMenuEvent,
    openModalEvent,
    closeModalEvent,
    addTagToNoteEvent,
    deleteWorkspaceTagEvent,
    renameWorkspaceTagEvent,
  ]);

  const createNote = useCreateNote();
  const t = useTranslations();
  const { toast } = useToast();
  const router = useRouter();

  const params = useParams();
  const slug = useMemo(() => (params?.slug as string[]) || [], [params?.slug]);
  const itemTagOpened = useCallback(
    (menuItem: MenuItem) => {
      return slug.length === 3 && slug[2] === encodeURIComponent(menuItem.name);
    },
    [slug]
  );

  return useCallback(
    async (menuItem: MenuItem, e: MouseEvent, useMousePos?: boolean) => {
      const items: ContextMenuItemData[] = [];
      const workspaceId = currentWorkspace.globalId;

      const extendedRenameMenuItem = Object.assign({}, renameMenuItem, {
        title: renameMenuItem.id ? t(contextItemLocMap[renameMenuItem.id]) : renameMenuItem.title,
        action: () => {
          openModal({
            title: t('sidebar.sidebar-context_menus.rename'),
            content: PopupType.RENAME,
            showCloseButton: true,
            closeButtonSize: 18,
            payload: {
              type: 'tag',
              value: menuItem.name,
              payloadType: PopupType.RENAME,
              themeColors: webnotesThemeColors,
              themeStyle: webnotesThemeStyle,
              onApply: (value: string) => {
                if (workspaceTags.tags.some((t) => t.name === value)) {
                  toast({ title: t('sidebar.sidebar-such_tag_already_exists') });
                  return;
                }
                renameWorkspaceTag({
                  workspaceId,
                  tag: menuItem.name,
                  newName: value,
                });
                if (itemTagOpened(menuItem)) {
                  router.push('/space/' + workspaceId + '/tag/' + encodeURIComponent(value));
                }
              },
            },
          });
        },
      });

      const extendedDeleteItem = Object.assign({}, deleteItem, {
        title: deleteItem.id ? t(contextItemLocMap[deleteItem.id]) : deleteItem.title,
        isSeparator: true,
        action: async () => {
          openModal({
            title: t('sidebar.sidebar-tag_removal'),
            content: PopupType.DIALOG,
            showCloseButton: true,
            closeButtonSize: 18,
            payload: {
              payloadType: PopupType.DIALOG,
              text: t('sidebar.sidebar-are_you_sure_delete', { itemName: menuItem.name }),
              buttons: [
                {
                  text: t('sidebar.sidebar-cancel'),
                },
                {
                  text: t('sidebar.sidebar-context_menus.delete'),
                  main: true,
                  onClick: () => {
                    if (itemTagOpened(menuItem)) {
                      const currentTagsNumber = workspaceTags.tags.length;
                      let deletedTagIndex = 0;
                      const workspaceTagsSorted = workspaceTags.tags.sort(tagsComparator);
                      workspaceTagsSorted.forEach((tag, index) => {
                        if (tag.name === menuItem.name) {
                          deletedTagIndex = index;
                        }
                      });
                      const nextTagFutureIndex: number | undefined =
                        currentTagsNumber > 1 ? Math.min(deletedTagIndex, currentTagsNumber - 2) : undefined;
                      const filteredTags = workspaceTagsSorted.filter((tag) => tag.name !== menuItem.name);
                      const nextTag =
                        nextTagFutureIndex !== undefined ? filteredTags[nextTagFutureIndex].name : undefined;

                      if (nextTag === undefined) {
                        router.push('/space/' + workspaceId + '/all');
                      } else {
                        router.push('/space/' + workspaceId + '/tag/' + encodeURIComponent(nextTag));
                      }
                    }

                    deleteWorkspaceTag({
                      workspaceId,
                      tagName: menuItem.name,
                    });

                    closeModal();
                  },
                },
              ],
            },
          });
        },
      });

      const extendedCreatePageMenuItem = Object.assign({}, createPageWithTagItem, {
        title: createPageWithTagItem.id ? t(contextItemLocMap[createPageWithTagItem.id]) : createPageWithTagItem.title,
        action: async () => {
          const createdNote = await createNote({
            workspaceId: currentWorkspace.globalId,
            parentId: 'default',
            redirectAfterCreate: true,
          });
          if (createdNote) {
            addTagToNote({ noteId: createdNote.globalId, tag: menuItem.name });
          }
        },
      });

      items.push(...[extendedRenameMenuItem, extendedCreatePageMenuItem, extendedDeleteItem]);
      const itemsFilteredByPriveleges = getFilterMenuItemsByPrivileges(
        wpCanManageMembers,
        workspaceAccess,
        userGranularPermissions,
        unleashFlags
      )(items);
      if (itemsFilteredByPriveleges.length !== 0) {
        openContextMenu({
          items: itemsFilteredByPriveleges,
          position: calculateContextMenuPosition(
            e.target,
            4,
            undefined,
            useMousePos ? { x: e.clientX, y: e.clientY } : undefined
          ),
          config: {
            position: UiContextMenuConfigPosition.BOTTOM_RIGHT,
            menuPadding: 0,
            theme: UiContextMenuTheme.LIGHT,
          },
        });
      }
    },
    [
      currentWorkspace.globalId,
      workspaceTags.tags,
      openContextMenu,
      wpCanManageMembers,
      workspaceAccess,
      userGranularPermissions,
      unleashFlags,
      openModal,
      closeModal,
      createNote,
      addTagToNote,
      deleteWorkspaceTag,
      renameWorkspaceTag,
      itemTagOpened,
      toast,
      router,
      t,
    ]
  );
};
